/**
 * Campaign Status Configuration — Single source of truth
 *
 * Maps each campaign status to its sidebar label, route, API endpoint/params,
 * feature flags, view type, and workflow type.
 *
 * workflowType describes which backend operation handles status transitions:
 *   - "first-time-approval"  → update-campaign-byadmin (slug gen, email, no drafts)
 *   - "draft-review"         → admin/approve-admin-review (applies drafts, launch date logic)
 *   - "post-publication"     → update-campaign-status (pause/close/reactivate)
 *   - "read-only"            → no status transitions from this view
 *
 * apiEndpoint preserves which backend endpoint each page currently calls.
 * The two list endpoints are NOT equivalent:
 *   - get-campaign-list-admin: direct status filter (superAdmin.controller.js:1992)
 *   - admin/get-campaign-details-list: portal/deal-flow semantics, expands
 *     APPROVED to include HOLD+CLOSED+COMMING_SOON (raiseCapital.controller.js:1430)
 */

export const CAMPAIGN_STATUSES = {
  "initial-approval": {
    statusCode: 1,
    label: "New Submissions",
    sidebarLabel: "Review Hub",
    route: "/initial-approve",
    permissionPrefix: "Campaign - Initial Approval",
    apiEndpoint: "get-campaign-list-admin",
    apiParams: {
      status: 1,
      campaign_status: 1,
      is_deleted: 0,
    },
    features: {
      hasDelete: false,
      hasRestore: false,
      hasPermanentDelete: false,
      hasDealFlowColumns: false,
      hasStatusColumn: false,
      hasInterestColumns: false,
      hasAccreditedColumn: false,
      hasTypeColumn: true,
      hasEmailColumn: true,
    },
    viewType: "initial-approval",
    workflowType: "first-time-approval",
  },

  "for-review": {
    statusCode: null, // filters by isForAdminReview + INREVIEW + HOLD via API special branch
    label: "Pending Edits",
    sidebarLabel: "Review Hub",
    route: "/for-review-campaign",
    permissionPrefix: "Campaign - For Review",
    apiEndpoint: "get-campaign-list-admin",
    apiParams: {
      status: 1,
      is_deleted: 0,
      isForAdminReview: 1,
      // Bug #15 fix — without an explicit campaign_status, the API returns
      // every row with isForAdminReview=1 regardless of state, so closed/
      // cancelled campaigns leak into the queue. Sending campaign_status=1
      // (INREVIEW) triggers the API's documented special branch at
      // superAdmin.controller.js (~line 2038) which expands the filter to
      // status IN (1, 8) — Initial Review + Paused — matching the queue's
      // UX intent: "campaigns the issuer flagged for admin re-review".
      campaign_status: 1,
    },
    features: {
      hasDelete: true,
      hasRestore: false,
      hasPermanentDelete: false,
      hasDealFlowColumns: true,
      hasStatusColumn: true,
      hasInterestColumns: false,
      hasAccreditedColumn: false,
      hasTypeColumn: true,
      hasEmailColumn: false,
    },
    viewType: "for-review",
    workflowType: "draft-review",
  },

  active: {
    statusCode: 3,
    label: "Active",
    route: "/active-campaign",
    permissionPrefix: "Campaign - Active",
    apiEndpoint: "get-campaign-list-admin",
    apiParams: {
      status: 1,
      campaign_status: 3,
      is_deleted: 0,
      isForAdminReview: 0,
    },
    features: {
      hasDelete: true,
      hasRestore: false,
      hasPermanentDelete: false,
      hasDealFlowColumns: false,
      hasStatusColumn: false,
      hasInterestColumns: false,
      hasAccreditedColumn: true,
      hasTypeColumn: true,
      hasEmailColumn: false,
    },
    viewType: "active",
    workflowType: "post-publication",
  },

  rejected: {
    statusCode: 2,
    label: "Rejected",
    route: "/rejected-campaign",
    permissionPrefix: "Campaign - Rejected",
    apiEndpoint: "get-campaign-list-admin",
    apiParams: {
      status: 1,
      campaign_status: 2,
      is_deleted: 0,
      isForAdminReview: 0,
    },
    features: {
      hasDelete: false,
      hasRestore: false,
      hasPermanentDelete: false,
      hasDealFlowColumns: true,
      hasStatusColumn: false,
      hasInterestColumns: false,
      hasAccreditedColumn: false,
      hasTypeColumn: false,
      hasEmailColumn: false,
    },
    viewType: "read-only",
    workflowType: "read-only",
  },

  completed: {
    statusCode: 4,
    label: "Completed",
    route: "/completed-campaign",
    permissionPrefix: "Campaign - Completed",
    apiEndpoint: "get-campaign-list-admin",
    apiParams: {
      status: 1,
      campaign_status: 4,
      is_deleted: 0,
      isForAdminReview: 0,
    },
    features: {
      hasDelete: true,
      hasRestore: false,
      hasPermanentDelete: false,
      hasDealFlowColumns: true,
      hasStatusColumn: false,
      hasInterestColumns: false,
      hasAccreditedColumn: false,
      hasTypeColumn: false,
      hasEmailColumn: false,
    },
    // DECISION 4 (2026-08-09): its own viewType, so CompletedActions reaches
    // ONLY completed campaigns. `read-only` is shared with rejected, closed,
    // draft, cancelled and deleted — remapping it would have offered Re-open on
    // all six. viewType is consumed in exactly one place
    // (CampaignViewPage's ACTION_PANELS lookup, with a ReadOnlyActions
    // fallback), so this is a panel change and nothing else.
    viewType: "completed",
    workflowType: "read-only",
  },

  deleted: {
    statusCode: 5,
    label: "Deleted",
    route: "/deleted-campaign",
    permissionPrefix: "Campaign - Deleted",
    apiEndpoint: "get-campaign-list-admin",
    apiParams: {
      status: 1,
      campaign_status: 5,
      is_deleted: 1,
    },
    features: {
      hasDelete: false,
      hasRestore: true,
      hasPermanentDelete: true,
      hasDealFlowColumns: true,
      hasStatusColumn: false,
      hasInterestColumns: false,
      hasAccreditedColumn: false,
      hasTypeColumn: false,
      hasEmailColumn: false,
    },
    viewType: "read-only",
    workflowType: "read-only",
  },

  draft: {
    statusCode: 6,
    label: "Draft",
    route: "/draft-campaign",
    permissionPrefix: "Campaign - Draft",
    apiEndpoint: "get-campaign-list-admin",
    apiParams: {
      status: 1,
      campaign_status: 6,
      is_deleted: 0,
      isForAdminReview: 0,
    },
    features: {
      hasDelete: true,
      hasRestore: false,
      hasPermanentDelete: false,
      hasDealFlowColumns: true,
      hasStatusColumn: false,
      hasInterestColumns: false,
      hasAccreditedColumn: false,
      hasTypeColumn: false,
      hasEmailColumn: false,
    },
    viewType: "read-only",
    workflowType: "read-only",
  },

  closed: {
    statusCode: 7,
    label: "Closed",
    route: "/closed-campaign",
    permissionPrefix: "Campaign - Closed Campaigns",
    apiEndpoint: "admin/get-campaign-details-list",
    apiParams: {
      campaign_status: 7,
      sortBy: "",
    },
    features: {
      hasDelete: false,
      hasRestore: false,
      hasPermanentDelete: false,
      hasDealFlowColumns: true,
      hasStatusColumn: false,
      hasInterestColumns: false,
      hasAccreditedColumn: false,
      hasTypeColumn: false,
      hasEmailColumn: false,
    },
    viewType: "read-only",
    workflowType: "read-only",
  },

  paused: {
    statusCode: 8,
    label: "Paused",
    route: "/paused-campaign",
    permissionPrefix: "Campaign - Paused campaigns",
    apiEndpoint: "admin/get-campaign-details-list",
    apiParams: {
      campaign_status: 8,
      sortBy: "",
    },
    features: {
      hasDelete: true,
      hasRestore: false,
      hasPermanentDelete: false,
      hasDealFlowColumns: true,
      hasStatusColumn: false,
      hasInterestColumns: false,
      hasAccreditedColumn: false,
      hasTypeColumn: false,
      hasEmailColumn: false,
    },
    viewType: "paused",
    workflowType: "post-publication",
  },

  "coming-soon": {
    statusCode: 9,
    label: "Upcoming",
    route: "/coming-soon-campaign",
    permissionPrefix: "Campaign - Upcoming Campaigns",
    apiEndpoint: "admin/get-campaign-details-list",
    apiParams: {
      status: 1,
      campaign_status: 9,
      sortBy: "",
      isForAdminReview: 0,
    },
    features: {
      hasDelete: false,
      hasRestore: false,
      hasPermanentDelete: false,
      hasDealFlowColumns: false,
      hasStatusColumn: false,
      hasInterestColumns: true,
      hasAccreditedColumn: false,
      hasTypeColumn: false,
      hasEmailColumn: false,
    },
    viewType: "coming-soon",
    workflowType: "post-publication",
  },

  cancelled: {
    statusCode: 10,
    label: "Cancelled",
    route: "/cancelled-campaign",
    permissionPrefix: "Campaign - Cancelled",
    apiEndpoint: "admin/get-campaign-details-list",
    apiParams: {
      campaign_status: 10,
      sortBy: "",
    },
    features: {
      hasDelete: false,
      hasRestore: false,
      hasPermanentDelete: false,
      hasDealFlowColumns: true,
      hasStatusColumn: false,
      hasInterestColumns: false,
      hasAccreditedColumn: false,
      hasTypeColumn: false,
      hasEmailColumn: false,
    },
    viewType: "read-only",
    workflowType: "read-only",
  },
};

/**
 * Look up a status config by its route path.
 * @param {string} pathname - e.g. "/active-campaign" or "/active-campaign/123"
 * @returns {[string, object]|null} - [statusKey, config] or null
 */
export function getConfigByRoute(pathname) {
  for (const [key, config] of Object.entries(CAMPAIGN_STATUSES)) {
    if (pathname === config.route || pathname.startsWith(config.route + "/")) {
      return [key, config];
    }
  }
  return null;
}

/**
 * Look up a status config by its key.
 * @param {string} statusKey - e.g. "active", "for-review"
 * @returns {object|null}
 */
export function getConfigByKey(statusKey) {
  return CAMPAIGN_STATUSES[statusKey] || null;
}
